import { css, Theme } from '@emotion/react';
import {
  AccountCircle,
  Assignment,
  CardMembership,
  EmojiEvents,
  Lock,
  People,
} from '@mui/icons-material';
import { useMemo } from 'react';

import {
  SettingsSidebar as SettingsSidebarList,
  SettingsSidebarItem as SettingsSidebarListItem,
} from 'shared/components/navigation';
import { Box } from 'shared/components/ui';
import { ROUTES } from 'shared/constants';
import { useCurrentUser, useFeatureEnabled } from 'shared/hooks';

import { SettingsSidebarUserProfile } from './SettingsSidebarUserProfile';

const containerCss = (theme: Theme) => css`
  border-right-style: solid;
  border-right-width: 1px;
  border-right-color: ${theme.palette.divider};
`;

const MAIN_SIDEBAR_LIST_DATA = [
  {
    title: 'Account Information',
    icon: <AccountCircle />,
    route: ROUTES.user.settings.information,
  },
  {
    title: 'Sign-in & Security',
    icon: <Lock />,
    route: ROUTES.user.settings.security,
  },
];

const ADMIN_MAIN_SIDEBAR_LIST_DATA = [
  {
    title: 'Account Information',
    icon: <AccountCircle />,
    route: ROUTES.user.settings.information,
  },
];

export const SettingsSidebar = ({ isAdminRoute = false }: { isAdminRoute: boolean }) => {
  const { user, loading: loadingUser } = useCurrentUser();
  const { enabled: showRewardingAccount } = useFeatureEnabled('rewardActions');
  const { enabled: showMemberships } = useFeatureEnabled('communityMarketplace');

  const additionalSidebarListData: SettingsSidebarListItem[] = useMemo(() => {
    const actions: SettingsSidebarListItem[] = [
      { title: 'Groups', icon: <People />, route: ROUTES.user.settings.groups },
      {
        title: 'Edit Public Profile',
        icon: <Assignment />,
        route: ROUTES.user.settings.profile,
      },
    ];

    if (showRewardingAccount) {
      actions.push({
        title: 'Rewards Account',
        icon: <EmojiEvents />,
        route: ROUTES.user.settings.rewarding,
      });
    }

    if (showMemberships) {
      actions.push({
        title: 'My Memberships',
        icon: <CardMembership />,
        route: ROUTES.user.settings.memberships,
      });
    }

    return actions;
  }, [showRewardingAccount, showMemberships]);

  return (
    <Box css={containerCss}>
      <SettingsSidebarUserProfile user={user} loading={loadingUser} />

      <SettingsSidebarList
        items={isAdminRoute ? ADMIN_MAIN_SIDEBAR_LIST_DATA : MAIN_SIDEBAR_LIST_DATA}
      />

      {!isAdminRoute && <SettingsSidebarList items={additionalSidebarListData} />}
    </Box>
  );
};
